import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useRef } from 'react';
import type { Blocker, Location } from 'react-router-dom';
import { useBlocker } from 'react-router-dom';

type BlockerCheckFn = (args: { currentLocation: Location; nextLocation: Location }) => boolean;

interface NavigationBlockerContextValue {
    register: (id: string, checkFn: BlockerCheckFn) => void;
    unregister: (id: string) => void;
    blocker: Blocker;
    activeBlockerId: string | null;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextValue | null>(null);

/**
 * Provides a single centralized `useBlocker` call for the entire app.
 *
 * React Router v6 only supports ONE active blocker at a time. When multiple
 * `useBlocker` hooks are mounted, only the last-registered one is checked.
 * This provider aggregates all blocking intents from CIPrompt instances
 * (and any other component) into a single `useBlocker` call, so that ANY
 * registered blocker can prevent navigation.
 */
export function NavigationBlockerProvider({ children }: { children: ReactNode }): ReactElement {
    const registryRef = useRef(new Map<string, BlockerCheckFn>());
    const activeBlockerIdRef = useRef<string | null>(null);

    const register = useCallback((id: string, checkFn: BlockerCheckFn) => {
        registryRef.current.set(id, checkFn);
    }, []);

    const unregister = useCallback((id: string) => {
        registryRef.current.delete(id);
        if (activeBlockerIdRef.current === id) {
            activeBlockerIdRef.current = null;
        }
    }, []);

    // Single blocker function that checks ALL registered blockers.
    // Uses a stable identity (empty deps) since it reads from refs.
    const blockerFn = useCallback(
        ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
            for (const [id, checkFn] of registryRef.current) {
                if (checkFn({ currentLocation, nextLocation })) {
                    activeBlockerIdRef.current = id;
                    return true;
                }
            }
            activeBlockerIdRef.current = null;
            return false;
        },
        []
    );

    const blocker = useBlocker(blockerFn);

    return (
        <NavigationBlockerContext.Provider
            value={{
                register,
                unregister,
                blocker,
                activeBlockerId: activeBlockerIdRef.current,
            }}
        >
            {children}
        </NavigationBlockerContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationBlockerContext(): NavigationBlockerContextValue | null {
    return useContext(NavigationBlockerContext);
}
